import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountTree,
  Add,
  Business,
  Download,
  Folder,
  Person,
  Stop,
} from '@mui/icons-material';
import ReactFlow, {
  Background,
  Controls,
  Edge,
  Handle,
  MarkerType,
  Node,
  NodeProps,
  Position,
  ReactFlowInstance,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { toPng } from 'html-to-image';

import {
  IKiKontrollGesellschafter,
  IKiKontrollSpeichernResult,
} from '../Interfaces/IKi';
import {
  useKontrollstrukturSchritt,
  useKontrollstrukturSpeichern,
} from '../hooks/reactQuerys/KiQueries';
import {
  FIKTIV_TEXT,
  KNOTEN_BREITE,
  KNOTEN_HOEHE,
  KontrollGraph,
  KontrollKnoten,
  MAX_ABRUFE,
  MAX_TIEFE,
  anordnen,
  berichtBauen,
  durchrechnen,
  gesellschafterEintragen,
  hatLeitung,
  kantenText,
  knotenSetzen,
  kontrollStore,
  leiterText,
  leitungHoehe,
  leitungKey,
  natuerlichePersonen,
  neuerGraph,
  prozent,
  wirtschaftlichBerechtigte,
} from './kontrollstruktur';
import { KiVorgangContext } from './KiVorgangContext';

/** Start aus einer schon gelesenen Liste: nur die Gesellschaften darin
 *  sind noch abzurufen. */
export const startAusListe = (
  wurzel: IKiKontrollGesellschafter,
  gesellschafter: IKiKontrollGesellschafter[],
  quelle?: string | null
): KontrollGraph => {
  const start = neuerGraph(wurzel, quelle);
  return gesellschafterEintragen(start, start.wurzel, gesellschafter).graph;
};

/** Start nur mit der Firma: zuerst wird ihre eigene Liste abgerufen. */
export const startNurWurzel = (
  wurzel: IKiKontrollGesellschafter
): KontrollGraph => {
  const start = neuerGraph(wurzel);
  return knotenSetzen(start, start.wurzel, { status: 'offen' });
};

interface IProps {
  start: KontrollGraph;
  /** Wohin speichern; ohne Angabe der Vorgang, auf dessen Seite der Chat ist. */
  idLegalTransaction?: number | null;
  vorgangTitel?: string | null;
  /** Die Gesellschaft als Mandant, falls bekannt. */
  idLegalClient?: number | null;
  /** Ablage-Karte: die Frage nach dem Speichern sofort stellen. */
  speichernFragen?: boolean;
}

const STATUS_TEXT: Record<string, string> = {
  offen: 'wartet',
  laeuft: 'wird geprüft …',
  nicht_gefunden: 'nicht gefunden',
  mehrdeutig: 'mehrdeutig',
  nicht_ermittelbar: 'nicht ermittelbar',
  fehler: 'Fehler',
  grenze: 'Grenze erreicht',
};

const farbe = (k: KontrollKnoten): string => {
  if (k.art === 'person') {
    return '#2e7d32';
  }
  if (k.status === 'ok') {
    return '#1565c0';
  }
  if (k.status === 'laeuft' || k.status === 'offen') {
    return '#9e9e9e';
  }
  return '#ed6c02';
};

const fehlerText = (exception: unknown, rueckfall: string): string =>
  exception instanceof Error && exception.message
    ? exception.message.replace(/^\d{3} [^:]+: /, '')
    : rueckfall;

/** Ein Kasten im Organigramm. */
const KontrollKnotenBox: React.FC<NodeProps<KontrollKnoten>> = ({ data }) => (
  <Box
    sx={{
      width: KNOTEN_BREITE,
      height: KNOTEN_HOEHE,
      border: 2,
      borderColor: farbe(data),
      borderStyle: data.status === 'offen' ? 'dashed' : 'solid',
      borderRadius: 1.5,
      bgcolor: 'background.paper',
      px: 1,
      py: 0.5,
      overflow: 'hidden',
    }}
  >
    <Handle type='target' position={Position.Top} style={{ opacity: 0 }} />
    <Stack direction='row' spacing={0.5} alignItems='center'>
      {data.art === 'person' ? (
        <Person fontSize='small' sx={{ color: farbe(data) }} />
      ) : (
        <Business fontSize='small' sx={{ color: farbe(data) }} />
      )}
      <Typography variant='body2' sx={{ fontWeight: 600 }} noWrap>
        {data.bezeichnung}
      </Typography>
      {data.status === 'laeuft' && <CircularProgress size={12} />}
    </Stack>
    <Typography variant='caption' color='text.secondary' noWrap component='div'>
      {data.art === 'person'
        ? data.detail || 'natürliche Person'
        : [data.register, data.quelle].filter(Boolean).join(' · ') ||
          (data.status ? STATUS_TEXT[data.status] : '')}
    </Typography>
    {data.art === 'gesellschaft' && data.status && data.status !== 'ok' && (
      <Typography
        variant='caption'
        component='div'
        noWrap
        sx={{ color: farbe(data) }}
        title={data.grund ?? undefined}
      >
        {STATUS_TEXT[data.status]}
        {data.grund ? ` – ${data.grund}` : ''}
      </Typography>
    )}
    <Handle type='source' position={Position.Bottom} style={{ opacity: 0 }} />
    <Handle
      type='source'
      id='rechts'
      position={Position.Right}
      style={{ opacity: 0 }}
    />
  </Box>
);

/** Rechts neben einer Gesellschaft ohne natürliche Person dahinter. */
const LeitungBox: React.FC<
  NodeProps<KontrollKnoten & { fiktiv?: boolean }>
> = ({ data }) => (
  <Box
    sx={{
      width: KNOTEN_BREITE,
      height: leitungHoehe(data),
      border: 2,
      borderStyle: 'dashed',
      borderColor: data.fiktiv ? '#6a1b9a' : '#ed6c02',
      borderRadius: 1.5,
      bgcolor: 'background.paper',
      px: 1,
      py: 0.5,
      overflow: 'hidden',
    }}
  >
    <Handle
      type='target'
      id='links'
      position={Position.Left}
      style={{ opacity: 0 }}
    />
    <Typography
      variant='caption'
      component='div'
      sx={{ fontWeight: 600, color: data.fiktiv ? '#6a1b9a' : '#ed6c02' }}
      noWrap
    >
      {data.fiktiv
        ? 'Geschäftsleitung · fiktiv wirtschaftlich Berechtigte'
        : 'Geschäftsleitung'}
    </Typography>
    {(data.geschaeftsleiter ?? []).length === 0 ? (
      <Typography variant='caption' color='text.secondary' component='div'>
        nicht im Register — bitte ergänzen
      </Typography>
    ) : (
      (data.geschaeftsleiter ?? []).map((l, i) => (
        <Typography
          key={`${data.key}-leiter-${i}`}
          variant='caption'
          component='div'
          noWrap
          title={leiterText(l)}
        >
          {leiterText(l)}
          {l.manuell ? ' ✎' : ''}
        </Typography>
      ))
    )}
  </Box>
);

const NODE_TYPES = { kontroll: KontrollKnotenBox, leitung: LeitungBox };

/** Geschäftsleitung von Hand ergänzen — z. B. bei einer Körperschaft. */
const LeiterEingabe: React.FC<{
  onAdd: (_name: string, _funktion: string) => void;
}> = ({ onAdd }) => {
  const [offen, setOffen] = useState(false);
  const [name, setName] = useState('');
  const [funktion, setFunktion] = useState('');

  if (!offen) {
    return (
      <Button
        size='small'
        startIcon={<Add fontSize='small' />}
        onClick={() => setOffen(true)}
        sx={{ py: 0 }}
      >
        Geschäftsleitung ergänzen
      </Button>
    );
  }

  return (
    <Stack direction='row' spacing={0.5} alignItems='center' sx={{ mt: 0.5 }}>
      <TextField
        size='small'
        label='Name'
        value={name}
        onChange={e => setName(e.target.value)}
      />
      <TextField
        size='small'
        label='Funktion'
        placeholder='z. B. Präsident'
        value={funktion}
        onChange={e => setFunktion(e.target.value)}
      />
      <Button
        size='small'
        disabled={name.trim() === ''}
        onClick={() => {
          onAdd(name.trim(), funktion.trim());
          setName('');
          setFunktion('');
        }}
      >
        Hinzufügen
      </Button>
    </Stack>
  );
};

/**
 * Eigentümer- und Kontrollstruktur (#1336): von der Gesellschaft aus jede
 * beteiligte Gesellschaft im Register nachschlagen, bis natürliche Personen
 * herauskommen oder sich nichts mehr finden lässt — als Organigramm mit
 * Pfeilen und Anteilen.
 *
 * Endet die Kette an einer Gesellschaft (AG, Körperschaft, ausländisch …),
 * steht ihre Geschäftsleitung daneben. Findet sich keine natürliche Person
 * mit mehr als 25 %, wird die Geschäftsleitung der Ausgangsgesellschaft
 * abgerufen: sie ist dann der fiktive wirtschaftlich Berechtigte
 * (§ 3 Abs. 2 S. 5 GwG).
 *
 * Je Gesellschaft ein Aufruf (`/ki/kontrollstruktur/schritt`), nacheinander:
 * so wächst das Bild sichtbar, und jeder Aufruf bleibt im Rahmen eines
 * Registerabrufs. Begrenzt auf MAX_TIEFE Ebenen und MAX_ABRUFE Abrufe.
 */
export const KiKontrollstruktur: React.FC<IProps> = ({
  start,
  idLegalTransaction,
  vorgangTitel,
  idLegalClient,
  speichernFragen,
}) => {
  const { pruefeGesellschaft } = useKontrollstrukturSchritt();
  const { speichereKontrollstruktur, isPendingSpeichern } =
    useKontrollstrukturSpeichern();
  const geoeffneterVorgang = useContext(KiVorgangContext);
  const zielVorgang = idLegalTransaction ?? geoeffneterVorgang;

  const [graph, setGraph] = useState<KontrollGraph>(start);
  const [laeuft, setLaeuft] = useState(true);
  const [aktuell, setAktuell] = useState<string | null>(null);
  const [abrufe, setAbrufe] = useState(0);
  const [ablage, setAblage] = useState<'frage' | 'nein' | 'auswahl' | 'fertig'>(
    'frage'
  );
  const [ergebnis, setErgebnis] = useState<IKiKontrollSpeichernResult | null>(
    null
  );
  const [gewaehlt, setGewaehlt] = useState<number | ''>('');
  const [fehler, setFehler] = useState<string | null>(null);

  const abbruch = useRef(false);
  const gestartet = useRef(false);
  const bild = useRef<HTMLDivElement>(null);
  const flow = useRef<ReactFlowInstance | null>(null);
  // Für den einmaligen Lauf: Startgraph und Abruf über Refs, damit der
  // Effekt keine Abhängigkeiten hat und nicht erneut startet.
  const startGraph = useRef(start);
  const pruefen = useRef(pruefeGesellschaft);
  pruefen.current = pruefeGesellschaft;

  // Karte geschlossen → keine weiteren Abrufe. (StrictMode baut ab und
  // gleich wieder auf; darum beim Aufbau zurücksetzen.)
  const entfernt = useRef(false);
  useEffect(() => {
    entfernt.current = false;
    return () => {
      entfernt.current = true;
    };
  }, []);

  // Für „speichere die Struktur in Vorgang X" eine Antwort später.
  useEffect(() => {
    kontrollStore.set(graph);
  }, [graph]);

  // Die Kette abarbeiten — einmal beim Öffnen, Schritt für Schritt.
  useEffect(() => {
    // StrictMode ruft den Effekt doppelt auf — Registerabrufe nur einmal.
    if (gestartet.current) {
      return;
    }
    gestartet.current = true;
    let g = startGraph.current;
    let gezaehlt = 0;
    // Eine unterbrochene Prüfung macht dort weiter, wo sie stand.
    const schlange = Object.values(g.knoten)
      .filter(
        k =>
          k.art === 'gesellschaft' &&
          (k.status === 'offen' || k.status === 'laeuft')
      )
      .map(k => k.key);
    const weiter = () => !abbruch.current && !entfernt.current;

    const schritt = async (key: string, nurLeitung: boolean) => {
      const knoten = g.knoten[key];
      const e = knoten.eintrag;
      return pruefen.current({
        Firma: e?.Firma || knoten.bezeichnung,
        Rechtsform: e?.Rechtsform ?? null,
        Registergericht: e?.Registergericht ?? null,
        Registernummer: e?.Registernummer ?? null,
        ...(nurLeitung ? { NurGeschaeftsleitung: true } : {}),
      });
    };

    const lauf = async () => {
      while (schlange.length > 0 && weiter()) {
        const key = schlange.shift() as string;
        const knoten = g.knoten[key];

        if (knoten.ebene > MAX_TIEFE) {
          g = knotenSetzen(g, key, {
            status: 'grenze',
            grund: `mehr als ${MAX_TIEFE} Ebenen`,
          });
          setGraph(g);
          continue;
        }
        if (gezaehlt >= MAX_ABRUFE) {
          g = knotenSetzen(g, key, {
            status: 'grenze',
            grund: `mehr als ${MAX_ABRUFE} Abrufe je Prüfung`,
          });
          setGraph(g);
          continue;
        }

        g = knotenSetzen(g, key, { status: 'laeuft' });
        setGraph(g);
        setAktuell(`${knoten.bezeichnung} (Ebene ${knoten.ebene})`);
        gezaehlt += 1;
        setAbrufe(gezaehlt);

        try {
          const r = await schritt(key, false);
          g = knotenSetzen(g, key, {
            status: r.Status,
            grund:
              r.Grund ??
              (r.Status === 'ok' && r.Gesellschafter.length === 0
                ? 'keine Gesellschafter gefunden'
                : null),
            quelle: r.Quelle ?? null,
            register: r.Register ?? knoten.register,
            geschaeftsleiter: r.Geschaeftsleiter ?? [],
            leitungManuell: Boolean(r.LeitungManuell),
            // Für den Abruf der Geschäftsleitung später: das Register, wie
            // es jetzt aufgelöst ist.
            eintrag: knoten.eintrag && {
              ...knoten.eintrag,
              Registernummer: r.Register ?? knoten.eintrag.Registernummer,
              Registergericht:
                r.Registergericht ?? knoten.eintrag.Registergericht,
            },
          });
          if (r.Status === 'ok') {
            const erg = gesellschafterEintragen(g, key, r.Gesellschafter);
            g = erg.graph;
            schlange.push(...erg.neu);
          }
        } catch (exception) {
          g = knotenSetzen(g, key, {
            status: 'fehler',
            grund: fehlerText(exception, 'Abruf gescheitert'),
            leitungManuell: true,
          });
        }
        setGraph(g);
      }

      // Keine natürliche Person mit mehr als 25 %: die Geschäftsleitung der
      // Ausgangsgesellschaft ist der fiktive wirtschaftlich Berechtigte.
      const wurzel = g.knoten[g.wurzel];
      if (
        weiter() &&
        wirtschaftlichBerechtigte(g).berechtigte.length === 0 &&
        (wurzel.geschaeftsleiter ?? []).length === 0 &&
        !wurzel.leitungManuell
      ) {
        setAktuell(`Geschäftsleitung von ${wurzel.bezeichnung}`);
        setAbrufe(gezaehlt + 1);
        try {
          const r = await schritt(g.wurzel, true);
          g = knotenSetzen(g, g.wurzel, {
            geschaeftsleiter: r.Geschaeftsleiter ?? [],
            leitungManuell:
              r.Status !== 'ok' || (r.Geschaeftsleiter ?? []).length === 0,
          });
        } catch {
          g = knotenSetzen(g, g.wurzel, { leitungManuell: true });
        }
        setGraph(g);
      }

      setAktuell(null);
      setLaeuft(false);
    };

    void lauf();
    // Bewusst nur beim Öffnen: die Kette läuft einmal durch.
  }, []);

  const gwg = useMemo(() => wirtschaftlichBerechtigte(graph), [graph]);
  const fiktiv = !laeuft && gwg.berechtigte.length === 0;

  const { nodes, edges } = useMemo(() => {
    const pos = anordnen(graph);
    const n: Node[] = Object.values(graph.knoten).map(k => ({
      id: k.key,
      type: 'kontroll',
      position: pos[k.key],
      data: k,
      draggable: true,
    }));
    const e: Edge[] = graph.kanten.map(k => ({
      id: `${k.von}->${k.nach}`,
      source: k.von,
      target: k.nach,
      label: kantenText(k) || undefined,
      type: 'smoothstep',
      markerEnd: { type: MarkerType.ArrowClosed },
      labelBgPadding: [4, 2] as [number, number],
      labelStyle: { fontSize: 11, fontWeight: 600 },
    }));
    for (const k of Object.values(graph.knoten).filter(hatLeitung)) {
      n.push({
        id: leitungKey(k.key),
        type: 'leitung',
        position: pos[leitungKey(k.key)],
        data: { ...k, fiktiv: fiktiv && k.key === graph.wurzel },
        draggable: true,
      });
      e.push({
        id: `${k.key}->leitung`,
        source: k.key,
        sourceHandle: 'rechts',
        target: leitungKey(k.key),
        targetHandle: 'links',
        type: 'straight',
        style: { strokeDasharray: '4 3' },
      });
    }
    return { nodes: n, edges: e };
  }, [graph, fiktiv]);

  const personen = natuerlichePersonen(graph);
  const anteile = durchrechnen(graph);
  const wurzel = graph.knoten[graph.wurzel];
  const istBerechtigt = new Set(gwg.berechtigte.map(b => b.knoten.key));

  function leiterHinzufuegen(key: string, name: string, funktion: string) {
    setGraph(g =>
      knotenSetzen(g, key, {
        geschaeftsleiter: [
          ...(g.knoten[key].geschaeftsleiter ?? []),
          { Bezeichnung: name, Funktion: funktion || null, manuell: true },
        ],
      })
    );
  }

  async function organigrammAlsPng(): Promise<string | null> {
    if (!bild.current) {
      return null;
    }
    flow.current?.fitView();
    await new Promise(resolve => setTimeout(resolve, 150));
    return toPng(bild.current, {
      backgroundColor: '#ffffff',
      filter: element =>
        !(element as HTMLElement).classList?.contains('react-flow__controls'),
    });
  }

  async function alsBild() {
    const url = await organigrammAlsPng();
    if (url === null) {
      return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = `Kontrollstruktur ${wurzel.bezeichnung}.png`;
    a.click();
  }

  async function ablegen(idGewaehlt: number | null) {
    if (zielVorgang === null) {
      return;
    }
    setFehler(null);
    try {
      const r = await speichereKontrollstruktur({
        idLegalTransaction: zielVorgang,
        idLegalClient: idGewaehlt ?? idLegalClient ?? null,
        Bericht: berichtBauen(graph),
        Bild: await organigrammAlsPng(),
      });
      setErgebnis(r);
      setAblage(r.Status === 'gespeichert' ? 'fertig' : 'auswahl');
    } catch (exception) {
      setFehler(
        fehlerText(exception, 'Die Struktur konnte nicht abgelegt werden.')
      );
    }
  }

  const vorgangText = vorgangTitel
    ? `„${vorgangTitel}"`
    : ergebnis?.Vorgang
      ? `„${ergebnis.Vorgang}"`
      : 'dieses Vorgangs';

  return (
    <Paper variant='outlined' sx={{ p: 1.5, mt: 1 }}>
      <Stack spacing={1}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <AccountTree fontSize='small' color='primary' />
          <Typography variant='subtitle2' sx={{ flexGrow: 1 }}>
            Eigentümer- und Kontrollstruktur {wurzel.bezeichnung}
          </Typography>
          {laeuft ? (
            <Button
              size='small'
              color='inherit'
              startIcon={<Stop />}
              onClick={() => {
                abbruch.current = true;
              }}
            >
              Anhalten
            </Button>
          ) : (
            <Button
              size='small'
              startIcon={<Download />}
              onClick={() => void alsBild()}
            >
              Als Bild speichern
            </Button>
          )}
        </Stack>

        {laeuft && (
          <Stack direction='row' spacing={1} alignItems='center'>
            <CircularProgress size={14} />
            <Typography variant='caption' color='text.secondary'>
              {aktuell ? `Prüfe ${aktuell} im Register …` : 'Prüfung beginnt …'}{' '}
              ({abrufe} von höchstens {MAX_ABRUFE} Abrufen, je bis zu anderthalb
              Minuten)
            </Typography>
          </Stack>
        )}

        <Box
          ref={bild}
          sx={{
            height: 460,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            onInit={instanz => {
              flow.current = instanz;
            }}
            fitView
            nodesConnectable={false}
            proOptions={{ hideAttribution: true }}
          >
            <Controls showInteractive={false} />
            <Background gap={12} size={1} />
          </ReactFlow>
        </Box>

        <Typography variant='subtitle2'>
          Wirtschaftlich Berechtigte (§ 3 GwG)
        </Typography>
        {gwg.berechtigte.length > 0 ? (
          <List dense disablePadding>
            {gwg.berechtigte.map(b => (
              <ListItem key={`wb-${b.knoten.key}`} disableGutters>
                <ListItemText
                  primary={b.knoten.bezeichnung}
                  secondary={[b.knoten.detail, b.grund]
                    .filter(Boolean)
                    .join(' · ')}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                />
                <Chip
                  size='small'
                  color='warning'
                  label={`${prozent(b.anteil)}`}
                />
              </ListItem>
            ))}
          </List>
        ) : laeuft ? (
          <Typography variant='body2' color='text.secondary'>
            Noch keine ermittelt.
          </Typography>
        ) : (
          <Alert severity='info' sx={{ py: 0.5 }}>
            <Typography variant='body2'>
              {FIKTIV_TEXT(wurzel.bezeichnung)}
            </Typography>
            {(wurzel.geschaeftsleiter ?? []).length > 0 ? (
              <Typography variant='body2' sx={{ mt: 0.5, fontWeight: 600 }}>
                {(wurzel.geschaeftsleiter ?? []).map(leiterText).join(', ')}
              </Typography>
            ) : (
              <>
                <Typography variant='caption' component='div' sx={{ mt: 0.5 }}>
                  Die Geschäftsleitung von {wurzel.bezeichnung} ließ sich nicht
                  aus dem Register ermitteln.
                </Typography>
                <LeiterEingabe
                  onAdd={(name, funktion) =>
                    leiterHinzufuegen(graph.wurzel, name, funktion)
                  }
                />
              </>
            )}
          </Alert>
        )}
        {gwg.unsicher && (
          <Typography variant='caption' color='warning.main'>
            Nicht alle Anteile sind bekannt (z. B. KG ohne Prozente) — das
            Ergebnis kann sich ändern.
          </Typography>
        )}

        {gwg.offeneEnden.length > 0 && (
          <Alert severity='warning' sx={{ py: 0 }}>
            <Typography
              variant='caption'
              component='div'
              sx={{ fontWeight: 600 }}
            >
              Hier endet die Kette ohne natürliche Person:
            </Typography>
            <Stack component='ul' sx={{ m: 0, pl: 2 }}>
              {gwg.offeneEnden.map(k => (
                <Typography component='li' variant='caption' key={k.key}>
                  {k.bezeichnung}
                  {typeof anteile[k.key]?.anteil === 'number'
                    ? ` (${prozent(anteile[k.key].anteil)})`
                    : ''}
                  : {STATUS_TEXT[k.status ?? 'offen'] ?? k.status}
                  {k.grund ? ` – ${k.grund}` : ''}
                  {(k.geschaeftsleiter ?? []).length > 0 && (
                    <>
                      <br />
                      Geschäftsleitung:{' '}
                      {(k.geschaeftsleiter ?? []).map(leiterText).join(', ')}
                    </>
                  )}
                  {!laeuft && (
                    <LeiterEingabe
                      onAdd={(name, funktion) =>
                        leiterHinzufuegen(k.key, name, funktion)
                      }
                    />
                  )}
                </Typography>
              ))}
            </Stack>
            {gwg.berechtigte.length === 0 && (
              <Typography variant='caption' component='div' sx={{ mt: 0.5 }}>
                Hinter diesen Gesellschaften kann noch jemand stehen — bitte das
                Transparenzregister einsehen.
              </Typography>
            )}
          </Alert>
        )}

        <Typography variant='subtitle2'>
          Natürliche Personen in der Kette
        </Typography>
        {personen.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            {laeuft ? 'Noch keine gefunden.' : 'Keine gefunden.'}
          </Typography>
        ) : (
          <List dense disablePadding>
            {personen.map(p => (
              <ListItem key={p.knoten.key} disableGutters>
                <ListItemText
                  primary={p.knoten.bezeichnung}
                  secondary={p.knoten.detail}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
                <Stack direction='row' spacing={0.5}>
                  <Chip
                    size='small'
                    variant='outlined'
                    label={
                      p.anteil === null
                        ? 'Anteil unbekannt'
                        : `durchgerechnet ${p.unvollstaendig ? 'mind. ' : ''}${prozent(p.anteil)}`
                    }
                  />
                  {istBerechtigt.has(p.knoten.key) && (
                    <Chip
                      size='small'
                      color='warning'
                      label='wirtschaftlich berechtigt'
                    />
                  )}
                </Stack>
              </ListItem>
            ))}
          </List>
        )}

        {!laeuft && zielVorgang !== null && ablage === 'frage' && (
          <Paper variant='outlined' sx={{ p: 1, borderColor: 'primary.light' }}>
            <Stack spacing={1}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <Folder fontSize='small' color='primary' />
                <Typography variant='body2'>
                  {speichernFragen
                    ? `Soll ich diese Kontrollstruktur im GwG-Ordner von ${vorgangText} ablegen?`
                    : `Soll ich die Kontrollstruktur im GwG-Ordner ${vorgangTitel ? `von ${vorgangText}` : 'dieses Vorgangs'} ablegen?`}
                </Typography>
              </Stack>
              <Typography variant='caption' color='text.secondary'>
                Als PDF mit Organigramm, als „Eigentümer-/Kontrollstruktur" bei{' '}
                {wurzel.bezeichnung} — der GwG-Ordner zeigt sie von dort.
              </Typography>
              {fehler !== null && <Alert severity='error'>{fehler}</Alert>}
              <Stack direction='row' spacing={1} justifyContent='flex-end'>
                <Button
                  size='small'
                  color='inherit'
                  disabled={isPendingSpeichern}
                  onClick={() => setAblage('nein')}
                >
                  Nein, danke
                </Button>
                <Button
                  size='small'
                  variant='contained'
                  startIcon={
                    isPendingSpeichern ? (
                      <CircularProgress size={16} color='inherit' />
                    ) : (
                      <Folder />
                    )
                  }
                  disabled={isPendingSpeichern}
                  onClick={() => void ablegen(null)}
                >
                  Im GwG-Ordner ablegen
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        {ablage === 'auswahl' && ergebnis?.Status === 'auswahl' && (
          <Alert severity='warning'>
            <Typography variant='body2'>
              {wurzel.bezeichnung} ist in „{ergebnis.Vorgang}" nicht eindeutig
              als Beteiligte zu finden. Bei wem soll die Struktur liegen?
            </Typography>
            {ergebnis.Kandidaten.length === 0 ? (
              <Typography variant='caption'>
                Am Vorgang ist keine juristische Person beteiligt — bitte die
                Gesellschaft dort zuerst als Beteiligte aufnehmen.
              </Typography>
            ) : (
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                sx={{ mt: 1 }}
              >
                <TextField
                  select
                  size='small'
                  sx={{ minWidth: 260 }}
                  label='Gesellschaft'
                  value={gewaehlt}
                  onChange={e => setGewaehlt(Number(e.target.value))}
                >
                  {ergebnis.Kandidaten.map(k => (
                    <MenuItem
                      key={`ks-k-${k.idLegalClient}`}
                      value={k.idLegalClient}
                    >
                      {k.Bezeichnung ?? `Mandant ${k.idLegalClient}`}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  size='small'
                  variant='contained'
                  disabled={gewaehlt === '' || isPendingSpeichern}
                  onClick={() =>
                    void ablegen(gewaehlt === '' ? null : gewaehlt)
                  }
                >
                  Ablegen
                </Button>
              </Stack>
            )}
            {fehler !== null && <Alert severity='error'>{fehler}</Alert>}
          </Alert>
        )}

        {ablage === 'fertig' && ergebnis?.Status === 'gespeichert' && (
          <Alert severity='success'>
            Abgelegt: „Eigentümer-/Kontrollstruktur"
            {ergebnis.Version ? ` (Version ${ergebnis.Version})` : ''} bei{' '}
            {ergebnis.Bezeichnung}.
            {ergebnis.GwGOrdner
              ? ` Sie steht im GwG-Ordner von „${ergebnis.Vorgang}".`
              : ` „${ergebnis.Vorgang}" hat noch keinen GwG-Ordner — sie liegt am Mandanten und erscheint dort, sobald der Ordner angelegt ist.`}
          </Alert>
        )}

        {!laeuft && zielVorgang === null && (
          <Typography variant='caption' color='text.secondary'>
            Zum Ablegen sagen Sie Karl z. B. „Speichere die Struktur in Vorgang
            …".
          </Typography>
        )}

        <Typography variant='caption' color='text.secondary'>
          Aus dem Handelsregister, gelesen von der KI — bitte gegen die
          Dokumente prüfen. Kontrolle auf andere Weise (Stimmbindung, Treuhand,
          Sonderrechte) zeigt das Register nicht; die Feststellung des
          wirtschaftlich Berechtigten bleibt beim Notar.
        </Typography>
      </Stack>
    </Paper>
  );
};
